import {
	Currency,
	type Dtos,
	FinancialPeriod,
	type IncomeStatementId,
	LINE_ONLY_POLICY,
	normalizeFormulaForSection,
	type SuretyAccountId,
} from "@inrev/common";
import { Option } from "@inrev/core";
import {
	useCreateIncomeStatementLineItem,
	useDeleteIncomeStatementLineItems,
	useRenameIncomeStatementLineItem,
	useUpdateIncomeStatement,
} from "@portal/api/financials";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { type FC, useCallback, useMemo } from "react";
import { FinancialDataGrid } from "../ui/FinancialDataGrid/FinancialDataGrid";
import type { NormalizeAdjustment } from "../ui/FinancialDataGrid/types";
import {
	ADMIN_VIEW_OPTIONS,
	type AdjustmentOverrideArgs,
	type CreateRowArgs,
	type DeleteRowArgs,
	type GivenOverrideArgs,
	type GridTableActionToggle,
	type PeriodViewModel,
	READ_ONLY_VIEW_OPTIONS,
	type RenameRowArgs,
	type StatementMatrixViewModel,
	ViewModeEnum,
} from "../ui/FinancialDataGrid/types";
import { collectExpandableRowIds } from "../ui/FinancialDataGrid/utils/collectExpandableRowIds";
import { useFinancialsPeriodSelector } from "./FinancialsPeriodSelectorProvider";
import { IncomeStatementPeriodHeaderContextMenu } from "./IncomeStatementPeriodHeaderContextMenu";
import { filterStatementViewModelPeriods } from "./utils/filterStatementViewModelPeriods";
import { mergeIncomeStatementWithEdits } from "./utils/mergeIncomeStatementWithEdits";

type IncomeStatementGridProps = {
	accountId: SuretyAccountId;
	baseViewModel: StatementMatrixViewModel<IncomeStatementId>;
};

// The income statement does not expose the hide-empty toggle, so empty revenue
// line items are always hidden — only the period-over-period change toggle shows.
const INCOME_STATEMENT_TABLE_ACTION_TOGGLES: readonly GridTableActionToggle[] = ["ppChange"];

type ParsedIncomeStatementRow =
	| { field: "revenue"; label: string }
	| { field: FinancialPeriod.IncomeStatementDirectFieldKey };

function parseRowId(rowId: string): ParsedIncomeStatementRow | null {
	if (rowId.startsWith("revenue::")) {
		const label = rowId.slice("revenue::".length);
		if (label === "") return null;
		return { field: "revenue", label };
	}
	if (rowId.includes("::")) return null;
	if (!FinancialPeriod.isIncomeStatementDirectFieldKey(rowId)) {
		return null;
	}
	return { field: rowId };
}

export const IncomeStatementGrid: FC<IncomeStatementGridProps> = ({ accountId, baseViewModel }) => {
	const { isAdmin } = useAuthenticatedUser();
	const { isIncomeStatementSelected } = useFinancialsPeriodSelector();
	const { updateIncomeStatement } = useUpdateIncomeStatement(accountId);
	const { deleteIncomeStatementLineItems } = useDeleteIncomeStatementLineItems(accountId);
	const { createIncomeStatementLineItem } = useCreateIncomeStatementLineItem(accountId);
	const { renameIncomeStatementLineItem } = useRenameIncomeStatementLineItem(accountId);

	const filteredViewModel = useMemo(
		() => filterStatementViewModelPeriods(baseViewModel, isIncomeStatementSelected),
		[baseViewModel, isIncomeStatementSelected],
	);
	const expandableRowIds = useMemo(
		() => collectExpandableRowIds(filteredViewModel.rows),
		[filteredViewModel.rows],
	);

	const renderPeriodContextMenu = useCallback(
		(period: PeriodViewModel<IncomeStatementId>) => (
			<IncomeStatementPeriodHeaderContextMenu
				accountId={accountId}
				incomeStatementData={{
					id: period.id,
					fileId: period.fileId,
					reviewedAt: period.reviewedAt,
					periodEndDate: period.periodEndDate,
					periodType: period.periodType,
					preparationMethod: period.preparationMethod,
				}}
				isActive={period.isActive}
			/>
		),
		[accountId],
	);

	const handleGivenOverride = useCallback(
		async ({ rowId, periodId, value }: GivenOverrideArgs<IncomeStatementId>) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot save changes for this line item");
			}
			const values = value === null ? { value: null } : { value: Currency.round(value) };
			const body: Dtos.Admin.IncomeStatement.Update.Request =
				parsed.field === "revenue"
					? { field: "revenue", label: parsed.label, values }
					: { field: parsed.field, values };
			await updateIncomeStatement({ incomeStatementId: periodId, body });
		},
		[updateIncomeStatement],
	);

	const handleAdjustmentOverride = useCallback(
		async ({ rowId, periodId, adjustment }: AdjustmentOverrideArgs<IncomeStatementId>) => {
			const parsed = parseRowId(rowId);
			if (!parsed) {
				throw new Error("Cannot save changes for this line item");
			}
			const values = adjustment === null ? { formula: null } : { formula: adjustment };
			const body: Dtos.Admin.IncomeStatement.Update.Request =
				parsed.field === "revenue"
					? { field: "revenue", label: parsed.label, values }
					: { field: parsed.field, values };
			await updateIncomeStatement({ incomeStatementId: periodId, body });
		},
		[updateIncomeStatement],
	);

	const handleDeleteRow = useCallback(
		async ({ rowId }: DeleteRowArgs) => {
			const parsed = parseRowId(rowId);
			if (!parsed || parsed.field !== "revenue") {
				throw new Error("Cannot delete this line item");
			}
			await deleteIncomeStatementLineItems({
				field: "revenue",
				label: parsed.label,
				incomeStatementIds: filteredViewModel.periods.map((p) => p.id),
			});
		},
		[deleteIncomeStatementLineItems, filteredViewModel.periods],
	);

	const handleRenameRow = useCallback(
		async ({ rowId, newLabel }: RenameRowArgs) => {
			const parsed = parseRowId(rowId);
			if (!parsed || parsed.field !== "revenue") {
				throw new Error("Cannot rename this line item");
			}
			await renameIncomeStatementLineItem({
				field: "revenue",
				oldLabel: parsed.label,
				newLabel,
				incomeStatementIds: filteredViewModel.periods.map((p) => p.id),
			});
		},
		[renameIncomeStatementLineItem, filteredViewModel.periods],
	);

	const handleCreateRow = useCallback(
		async ({
			parentRowId,
			label,
			valuesByPeriod,
			adjustmentsByPeriod,
		}: CreateRowArgs<IncomeStatementId>) => {
			if (parentRowId !== "revenue") {
				throw new Error("Cannot add a line item here");
			}
			const periods = filteredViewModel.periods.flatMap((p) => {
				const value = valuesByPeriod[p.id];
				const formula = adjustmentsByPeriod[p.id];
				const hasValue = Option.isSome(value);
				if (!hasValue && !formula) return [];
				return [
					{
						incomeStatementId: p.id,
						...(hasValue ? { value: Currency.round(value) } : {}),
						...(formula ? { formula } : {}),
					},
				];
			});
			await createIncomeStatementLineItem({ field: "revenue", label, periods });
		},
		[createIncomeStatementLineItem, filteredViewModel.periods],
	);

	const normalizeAdjustment = useCallback<NormalizeAdjustment>(
		(_rowId, draft) => normalizeFormulaForSection(draft, LINE_ONLY_POLICY),
		[],
	);

	return (
		<FinancialDataGrid<IncomeStatementId>
			accountId={accountId}
			normalizeAdjustment={normalizeAdjustment}
			baseViewModel={filteredViewModel}
			mergeWithEdits={mergeIncomeStatementWithEdits}
			expandableRowIds={expandableRowIds}
			label="Income Statement"
			testId="income-statement-grid"
			emptyMessage="No income statement data for the selected periods."
			readOnly={!isAdmin}
			viewOptions={isAdmin ? ADMIN_VIEW_OPTIONS : READ_ONLY_VIEW_OPTIONS}
			initialView={isAdmin ? ViewModeEnum.All : ViewModeEnum.GivenAllowed}
			onGivenOverride={handleGivenOverride}
			onAdjustmentOverride={handleAdjustmentOverride}
			onDeleteRow={handleDeleteRow}
			onCreateRow={handleCreateRow}
			onRenameRow={handleRenameRow}
			renderPeriodContextMenu={renderPeriodContextMenu}
			tableActionToggles={INCOME_STATEMENT_TABLE_ACTION_TOGGLES}
		/>
	);
};
